import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createSampleComment,
  removeSampleComment,
  sampleCommentsShellQuery,
  sampleCommentsTableQuery,
  updateSampleComment,
  type SampleCommentRow,
} from '../../api/settingsEaseedeskSmallScreens'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Sample Comments.
 *
 * Element cu element fata de `version-3/easeedesk_settings/escalation/
 * escalation_settings/sample_comments.php`, si gest cu gest fata de
 * `sample_comments-1.js`: un tabel cerut de la server, doua ferestre si o
 * intrebare inainte de stergere.
 *
 * Butoanele de scriere apar dupa drepturi, ca in macheta (`canAccess(...)`
 * inainte de butonul de adaugare); serverul le refuza si el, drept pentru
 * drept.
 */
const PAGE = 25

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * `moment(source.created_at).format("D MMM YYYY, HH:mm")` - chiar randul din
 * `SampleCommentModel`. Coloana arata pana acum datetime-ul brut din baza
 * (`2026-09-16 14:32:07`), care nu e nici forma zonei, nici ce vedea omul in
 * portal.
 */
function whenText(value: string): string {
  const when = new Date(String(value ?? '').replace(' ', 'T'))
  if (Number.isNaN(when.getTime())) return ''
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  return `${when.getDate()} ${MONTHS[when.getMonth()]} ${when.getFullYear()}, ${hh}:${mm}`
}

export function SampleCommentsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(sampleCommentsShellQuery())
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<SampleCommentRow | null>(null)
  const [text, setText] = useState('')

  const table = useQuery(sampleCommentsTableQuery(search, start, PAGE))

  useEffect(() => {
    setText(edited?.comment ?? '')
  }, [edited])

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'sample_comments'] })
  }

  const save = useMutation({
    mutationFn: () => (edited !== null ? updateSampleComment(edited.id, text) : createSampleComment(text)),
    onSuccess: () => {
      toast('Sample Comment was saved!', 'Success')
      setCreating(false)
      setEdited(null)
      setText('')
      reload()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => removeSampleComment(id),
    onSuccess: () => {
      toast('Sample comment deleted.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="Sample Comments" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsTotal ?? 0

  return (
    <div>
      {/*
       * Butonul statea DUPA subtitlu si inainte de linie, deci cadea peste
       * capul listei. Locul lui e inaintea titlului, plutit la dreapta, cu
       * clasele zonei - regula asezata pe Global settings (9b257d20fc) si pe
       * Softsee (7d99ca3cee). Textul de sub titlu ramane cel al machetei,
       * "Manage services", cu tot cu potriveala lui: e ce scrie in portal.
       */}
      <SettingsHead
        strong="Sample Comments"
        subtitle={
          <>
            Manage <strong>services</strong>
          </>
        }
        tools={
          shell.data.canCreate ? (
            <button
              type="button"
              className="pull-right btn btn-success btn-sm"
              onClick={() => {
                setEdited(null)
                setText('')
                setCreating(true)
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> New sample comment
            </button>
          ) : undefined
        }
      />

      <div className="form-group" style={{ maxWidth: 320 }}>
        <input
          type="search"
          className="form-control input-sm"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setStart(0)
            setSearch(e.target.value)
          }}
        />
      </div>

      {table.error ? (
        <LoadFailed what="The sample comments" error={table.error} onRetry={() => void table.refetch()} />
      ) : !table.data ? (
        <Spinner />
      ) : (
        <table className="table table-striped table-hover table-bordered dataTable" width="100%" id="sample-comments">
          <thead>
            {/* Ordinea din `SampleCommentModel`: Comment, Created By, Created at, Actions. */}
            <tr>
              <th>Comment</th>
              <th width={200}>Created By</th>
              <th width={160}>Created at</th>
              <th width={140}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} id={`row_${row.id}`}>
                <td>{row.comment}</td>
                <td>{row.created_by}</td>
                <td>{whenText(row.created_at)}</td>
                <td>
                  {shell.data.canEdit && (
                    <button
                      type="button"
                      className="btn btn-xs btn-primary"
                      onClick={() => {
                        setCreating(false)
                        setEdited(row)
                      }}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>
                  )}{' '}
                  {shell.data.canDelete && (
                    /*
                     * Stergea din prima apasare, fara nicio intrebare. In
                     * portal e un swal cu "Are you sure?" si "Sample Comment
                     * will be lost." inainte de `destroySampleComment`;
                     * butonul se cheama acolo "Delete", cu acelasi semn.
                     */
                    <button
                      type="button"
                      className="btn btn-xs btn-danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (!window.confirm('Are you sure?\nSample Comment will be lost.')) return
                        remove.mutate(row.id)
                      }}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <div className="alert alert-info" style={{ marginBottom: 0 }}>
                    <i className="fas fa-info-circle" aria-hidden="true"></i> No sample comments.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="aright">
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start === 0}
            onClick={() => setStart(Math.max(0, start - PAGE))}
          >
            Previous
          </button>{' '}
          <span>
            {start + 1}-{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start + PAGE >= total}
            onClick={() => setStart(start + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        id={edited !== null ? 'modalEditSampleComment' : 'modalAddSampleComment'}
        labelledBy={edited !== null ? 'modalEditSampleCommentLabel' : 'modalAddSampleCommentLabel'}
        open={creating || edited !== null}
        onClose={() => {
          setCreating(false)
          setEdited(null)
        }}
        title={edited !== null ? 'Edit Sample Comment' : 'Add new sample comment'}
      >
        <form
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (text.trim() === '') return
            save.mutate()
          }}
        >
          <div className="form-group">
            <label className="control-label">
              Comment <span className="required">*</span>
            </label>
            <textarea
              className="form-control"
              name="sample_comment"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
            />
          </div>
          <div className="form-group aright">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => {
                setCreating(false)
                setEdited(null)
              }}
            >
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={text.trim() === '' || save.isPending}>
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
